// DispatchDeck — Person payment-card accessor (TASK-4865)
// ============================================================================
// `window.PersonCard` — the ONE way client code resolves a person's fuel-card
// PIN now that it is moving off the broadcast `people` collection into the
// admin/HR-gated sibling `tenants/{tid}/peopleCard/{personId}`.
//
// WHY THIS EXISTS. `firestore.rules` `people/{personId}` is
// `allow read: if canReadTenant(...)`, and canReadTenant folds in
// `hasDriverSession` — the rules file's own comment calls `people` "BROADCAST
// to every session incl. driver PINs". `hr.js saveEmploymentFields` wrote
// `fuelCardPin` straight onto that doc, and `buildEmployeeDetailHeaderHTML`
// PRINTED it in the clear. Measured 2026-09-09 (admin SDK, read-only, every
// tenant / 465 people): 15 people carry a fuel-card PIN, every one of them at
// `trippin-trucking` — so any driver who completed a normal PIN login on a
// shared yard tablet could raw-SDK enumerate all 15 live fuel-card PINs.
// Firestore cannot field-redact a read, so the ONLY fix is moving the field.
// Fourth instance of the family: peopleTax (SSN), peopleComp (TASK-1481 wages),
// peopleSecure (TASK-1441 driver PINs), and now the card PIN.
//
// WHAT MOVES, AND WHAT DELIBERATELY DOES NOT
//   MOVES:      fuelCardPin — a payment CREDENTIAL. It is the second factor at
//               the pump; with the physical card (or a cloned mag stripe) it
//               buys diesel. Nothing but the credential moves.
//   STAYS on `people`: hasCreditCard / creditCardLast4 / hasFuelCard /
//               fuelCardLast4. Three reasons, all load-bearing —
//                 (a) a last-4 is not a credential. PCI-DSS explicitly permits
//                     displaying the last four digits; it authorizes nothing.
//                 (b) `jobboard.js` (~:10549) reads `person.creditCardLast4`
//                     inside the driver/AP expense modal to pre-select the
//                     TASK-362 card picker. That code runs in a DRIVER session.
//                     Moving last-4 to an admin-gated collection would make
//                     that read permission-denied and the picker would silently
//                     stop auto-selecting — a regression with no error message,
//                     bought for no security gain.
//                 (c) `hasFuelCard` is set on 27 people across two tenants and
//                     drives the HR print row + the edit form's disclosure
//                     toggle; it reveals only that a card was issued.
//
// LOADING (personComp.js / payRates.js precedent — CLAUDE.md Pitfall #1):
// imported ONCE via a side-effect import from views/clock.js (which is in
// app.js's static import graph, so this evaluates at startup before any view
// handler runs). EVERY other module reads `window.PersonCard` at call time —
// never a second `?v=` import (two busters = two module instances = ghost
// state). firebase-config.js is imported with the SAME pinned `?v=` every
// other importer uses, so it stays one instance.
//
// 🔴 ON-DEMAND, NOT A SUBSCRIPTION — the ONE deliberate departure from
// personComp.js. PersonComp subscribes to the whole `peopleComp` collection
// because wages render in bulk across payroll lists. A fuel-card PIN renders
// in exactly ONE place, one person at a time (the Employee Details header),
// and it is a credential: pulling all 15 into every HR session's memory and
// then into the DOM is the same shoulder-surfing exposure this task is closing,
// just moved one gate inward. So: `load(tenantId, personId)` does a single
// getDoc, on an explicit Reveal, and caches only what was asked for.
//
// 🔴 DENIED ≠ ABSENT. A member without admin/hr/hr-directory gets
// permission-denied on the card doc. That is NOT "no PIN on file" — it is "you
// cannot know". The accessor records that state (`isDenied(personId)`), and
// the HR surface must say so instead of rendering an empty PIN, because an
// empty PIN is indistinguishable from a cleared one and an editor would then
// save the blank over the real value. That is the TASK-1191 wipe vector.
//
// MERGE SEMANTICS: a card field KEY PRESENT on the peopleCard doc (including
// an explicit null) wins over the person doc; an ABSENT key falls through to
// the legacy person-doc value. This makes the pre-migration state correct:
// nothing has a card doc yet, so every read falls through to exactly what it
// reads today, and the first save routes the PIN to the vault.
//
// TASK-9291 — MORE THAN TWO CARDS PER PERSON (Chris, 2026-09-22, while
// entering his real bank cards: "we need to be able to assign more than one
// credit card people"). Nine people at trippin-trucking were already at the
// two-slot ceiling. The tail beyond the two legacy slots is modelled as an
// ASSIGNMENT — `people/{pid}.additionalCardIds`, opaque tenant-registry ids,
// carrying NO last-4, NO name, NO bank and NO PIN. The matching per-card PIN
// map was built and then CUT on COD's 🔴; the block on CARD_SECURE_FIELDS
// below has the full reasoning and the exact rules change it waits on.
// Nothing about the two legacy slots moves, changes, or stops being written:
// 429 of 456 people carry no card at all and must keep rendering
// byte-identically, and 27 fuel assignments have no registry row to migrate
// to (measured 2026-09-22: ZERO cardType:'fuel' rows exist in any tenant).
//
// The pure core lives in `_personCardCoreFactory` (no Firebase, no DOM) so
// tests/person-card.unit.cjs can extract and run it in node:vm — the same
// harness pattern as tests/person-comp.unit.cjs.

import {
  db,
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from './firebase-config.js?v=1868';

// ── pure core (vm-testable, no Firebase) ────────────────────────────────────
function _personCardCoreFactory() {
  'use strict';

  // Keep in lockstep with scripts/migrate-fuelcardpin-to-peoplecard.cjs and
  // the firestore.rules peopleCard block / people create+update denylists.
  //
  // 🔴 TASK-9291 — A PER-CARD PIN MAP (`cardPins`) IS DELIBERATELY NOT HERE,
  // AND MUST NOT BE ADDED UNTIL firestore.rules CHANGES FIRST.
  //
  // It was designed, built and then CUT on COD's 🔴 (Adversary, echoed by
  // Rules&AuthZ, Blast-Radius, Domain, Secrets and the Independent reviewer,
  // 2026-09-22). The vault half was fine — the `peopleCard` block takes
  // arbitrary fields under admin||hr||hr-directory. The hole is on the OTHER
  // doc: `people` create/update is a DENYLIST, not an allowlist
  // (`hasOnly()` is never applied), and it names only `fuelCardPin` and the
  // `driverPin*` family. So the instant a field called `cardPins` holds real
  // pump PINs anywhere, ANY `isDispatcherOrHigher || hasModule('roster')`
  // client — a stale cached bundle, a raw SDK call, a future code path — can
  // land it on `people`, which `canReadTenant` broadcasts to every driver-PIN
  // kiosk session. That is TASK-4865 reopened under a key the denylist has
  // never heard of, and firestore.rules' own comment already rules out the
  // defence: "the client never writes it" is not a control.
  //
  // THE COMPANION RULES CHANGE, precisely: mirror the existing `fuelCardPin`
  // arms onto `cardPins` on BOTH the create and update arms of
  // `people/{personId}` — value-constrained, not a key ban, for the same
  // reason spelled out there (a key ban breaks every routine save from a
  // browser still running an older bundle). Once that ships, add `cardPins`
  // to CARD_SECURE_FIELDS and the HR per-card PIN UI is a small change.
  //
  // Nothing half-built is left behind on purpose: a `cardPinPatch` helper
  // whose output `pickCard` would silently drop is worse than no helper —
  // it fails OPEN and quiet, which is the one failure mode this file exists
  // to prevent. The ASSIGNMENT half (`people.additionalCardIds`) ships
  // without it and carries no credential at all.
  const CARD_SECURE_FIELDS = ['fuelCardPin'];

  // Fields that deliberately STAY on the broadcast person doc — enumerated so
  // a future edit that "moves the card fields" has to read the reasons above
  // before adding one of these to CARD_SECURE_FIELDS.
  // TASK-9291 adds `additionalCardIds` to this list: an ordered array of
  // OPAQUE tenant-registry ids for the cards a person holds beyond the two
  // legacy slots. It is on the broadcast doc deliberately and it is not a
  // widening of the leak — it carries no PAN, no last-4, no bank, no
  // cardholder name and no PIN. The ids point into
  // `tenants/{tid}.linesOfCredit`, an array the SAME driver-PIN session
  // already reads in full (it is the only source the jobboard expense picker
  // has), and `creditCardLast4` above already lets any such session join a
  // person to a registry row. Putting the list in this vault instead would
  // make the driver's read permission-denied and the expense picker would
  // silently stop offering the right card — reason (b) above, verbatim.
  const CARD_PUBLIC_FIELDS = ['hasCreditCard', 'creditCardLast4', 'hasFuelCard', 'fuelCardLast4', 'additionalCardIds'];

  // Per-person status, because loads are per-person:
  //   'idle'    never loaded
  //   'loading' getDoc in flight
  //   'ready'   loaded (doc may not exist — that is a real "no PIN on file")
  //   'denied'  permission-denied — the caller CANNOT know
  //   'error'   transient failure — also cannot know
  const state = { tenantId: null, map: new Map(), status: new Map() };

  // Subset of the secure card fields present on `src` (undefined keys dropped —
  // Firestore throws on undefined, and an undefined must never clobber a
  // stored value through a merge write). An explicit null IS kept: that is how
  // "clear the PIN" is expressed.
  function pickCard(src) {
    const out = {};
    if (!src) return out;
    CARD_SECURE_FIELDS.forEach((k) => {
      if (src[k] !== undefined) out[k] = src[k];
    });
    return out;
  }

  function get(personId) {
    return (personId && state.map.get(personId)) || null;
  }

  function status(personId) {
    return (personId && state.status.get(personId)) || 'idle';
  }

  function isDenied(personId) { return status(personId) === 'denied'; }

  // "Cannot know" covers denial AND a transient failure: both mean the caller
  // must not treat an empty value as "no PIN set".
  function cannotKnow(personId) {
    const s = status(personId);
    return s === 'denied' || s === 'error';
  }

  // The one resolver every read site uses. Card doc present → its keys win
  // (per-key, so a partial doc is safe); absent → the person doc's own legacy
  // field survives untouched (pre-migration behavior, byte-identical).
  function effective(person) {
    if (!person) return person;
    const card = person.id ? state.map.get(person.id) : null;
    if (!card) return person;
    const merged = Object.assign({}, person);
    CARD_SECURE_FIELDS.forEach((k) => {
      if (k in card) merged[k] = card[k];
    });
    return merged;
  }

  // The resolved PIN, or null. Never returns '' — an empty string and a null
  // both mean "no PIN", and callers should not have to know the difference.
  function pin(person) {
    const e = effective(person);
    const v = e && e.fuelCardPin;
    return (v === undefined || v === null || v === '') ? null : String(v);
  }

  // Does this person doc itself still carry the secure field? (The legacy
  // fallback signature. After the migration's --strip step this is false for
  // everyone, which is how the migration proves it finished.)
  function personHasOwnPin(person) {
    if (!person) return false;
    const v = person.fuelCardPin;
    return v !== undefined && v !== null && v !== '';
  }

  // 🔴 DENIED ≠ ABSENT — the per-person refusal predicate. True only when we
  // CANNOT KNOW the card doc AND the person doc carries no legacy value to
  // fall back on: the caller cannot distinguish "no PIN set" from "PIN hidden
  // from you", so the surface must say so rather than render a blank that a
  // save would then persist over the real value.
  function pinUnknown(person) {
    if (!person || !person.id) return false;
    if (!cannotKnow(person.id)) return false;
    return !personHasOwnPin(person);
  }

  // Snapshot plumbing (called by the Firebase wiring below; also directly by
  // unit tests to simulate loads/errors).
  function _applyDoc(personId, data) {
    if (!personId) return;
    if (data) state.map.set(personId, data);
    else state.map.delete(personId);
    state.status.set(personId, 'ready');
  }
  function _applyError(personId, code) {
    if (!personId) return;
    state.status.set(personId, code === 'permission-denied' ? 'denied' : 'error');
  }
  function _reset(tenantId) {
    state.tenantId = tenantId || null;
    state.map = new Map();
    state.status = new Map();
  }

  return {
    CARD_SECURE_FIELDS, CARD_PUBLIC_FIELDS, pickCard, get, status, isDenied,
    cannotKnow, effective, pin, personHasOwnPin, pinUnknown,
    _applyDoc, _applyError, _reset, _state: state,
  };
}

// ── Firebase wiring ─────────────────────────────────────────────────────────
const _core = _personCardCoreFactory();

// 🔴 GENERATION GUARD (COD Code Reviewer, 2026-09-09). `load()` and
// `writeCard()` both await a network round trip. Without this, a tenant switch
// that lands MID-FLIGHT would let the in-flight completion write the PREVIOUS
// tenant's credential into the fresh cache — the reset would look like it
// worked and then silently un-happen. Every completion re-checks the
// generation it started under and drops itself if the world moved.
let _gen = 0;

// Called by hr.js when its tenant subscription (re)initializes, so a tenant
// switch can never serve another tenant's cached PIN.
function setTenant(tenantId) {
  if (_core._state.tenantId === tenantId) return;
  _gen++;
  _core._reset(tenantId);
}

// On-demand single-person load. Returns { ok, code, found }.
//   ok:true  found:true   → a card doc exists and is cached
//   ok:true  found:false  → no card doc (a real "no PIN in the vault")
//   ok:false code:'permission-denied' → the caller CANNOT know (see pinUnknown)
//   ok:false code:<other> → transient; same "cannot know" treatment
async function load(tenantId, personId) {
  if (!tenantId || !personId) return { ok: false, code: 'bad-args', found: false };
  setTenant(tenantId);
  const myGen = _gen;
  _core._state.status.set(personId, 'loading');
  try {
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'peopleCard', personId));
    // Tenant moved while we were in flight — drop the result on the floor.
    if (myGen !== _gen) return { ok: false, code: 'stale-tenant', found: false };
    _core._applyDoc(personId, snap.exists() ? snap.data() : null);
    return { ok: true, code: null, found: snap.exists() };
  } catch (err) {
    const code = (err && err.code) || 'unknown';
    if (myGen !== _gen) return { ok: false, code: 'stale-tenant', found: false };
    _core._applyError(personId, code);
    if (code !== 'permission-denied') {
      console.warn('[PersonCard] peopleCard load failed for', personId, (err && err.message) || err);
    }
    return { ok: false, code, found: false };
  }
}

// Write helper — saveEmploymentFields calls this AFTER its person-doc write so
// the vault stays the source of truth. Merge semantics; only the secure card
// fields are ever written (pickCard drops everything else INCLUDING undefined,
// which Firestore would throw on).
//
// Returns { ok, code }:
//   ok:true                → landed (or nothing card-relevant to write)
//   ok:false code:'permission-denied' → the caller's access level can't write
//        the vault. Two ways: (a) this client shipped BEFORE the firestore.rules
//        peopleCard block (the rules MUST deploy first); (b) post-rules, an
//        editor without hr/hr-directory. Either way the caller shows a WARNING
//        with actionable copy — never silence, because if a vault doc already
//        exists it is now STALE and it WINS over the person doc for readers.
//   ok:false code:<other>  → transient failure; same drift risk, retry copy.
async function writeCard(tenantId, personId, fields) {
  if (!tenantId || !personId) return { ok: false, code: 'bad-args' };
  const payload = _core.pickCard(fields);
  if (!Object.keys(payload).length) return { ok: true, code: null }; // nothing card-relevant changed
  const myGen = _gen;
  try {
    const toWrite = Object.assign({}, payload, {
      personId,
      updatedAt: Timestamp.now(),
    });
    await setDoc(doc(db, 'tenants', tenantId, 'peopleCard', personId), toWrite, { merge: true });
    // Keep the local cache consistent so a Reveal right after a save shows the
    // value just written instead of a stale getDoc result — but ONLY if the
    // tenant did not change under us (generation guard).
    if (myGen === _gen) {
      const prev = _core.get(personId) || {};
      _core._applyDoc(personId, Object.assign({}, prev, payload));
    }
    return { ok: true, code: null };
  } catch (err) {
    console.warn('[PersonCard] peopleCard write failed for', personId, (err && err.message) || err);
    return { ok: false, code: (err && err.code) || 'unknown' };
  }
}

window.PersonCard = {
  CARD_SECURE_FIELDS: _core.CARD_SECURE_FIELDS,
  CARD_PUBLIC_FIELDS: _core.CARD_PUBLIC_FIELDS,
  pickCard: _core.pickCard,
  get: _core.get,
  status: _core.status,
  isDenied: _core.isDenied,
  cannotKnow: _core.cannotKnow,
  effective: _core.effective,
  pin: _core.pin,
  personHasOwnPin: _core.personHasOwnPin,
  pinUnknown: _core.pinUnknown,
  setTenant,
  load,
  writeCard,
};
